// WebSocket Connection Manager - Enhanced for v2.0 Server with Real-time Subscriptions
class WebSocketManager {
  constructor() {
    this.WS_URL = 'wss://dhakawitness.com';
    this.connection = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // Increased for better reliability
    this.isConnecting = false;
    this.connectionPromise = null;
    this.broadcastListeners = new Map(); // For broadcast event listeners
    
    // Enhanced JSON-RPC request management matching server capabilities
    this.pendingRequests = new Map(); // id -> {resolve, reject, timeout, priority, timestamp}
    this.requestId = 0; // Incrementing ID for JSON-RPC requests
    this.requestQueue = []; // Queue for rate-limited requests with priority
    this.lastRequestTime = 0;
    this.MIN_REQUEST_INTERVAL = 30; // 30ms between requests (2000/min = 33/sec)
    this.maxRequestsPerMinute = 1800; // Stay under server limit of 2000/min
    this.requestTimestamps = []; // Track recent requests for rate limiting
    
    // Circuit breaker pattern for request resilience
    this.circuitBreaker = {
      state: 'CLOSED', // CLOSED, OPEN, HALF_OPEN
      failures: 0,
      threshold: 5, // Open after 5 consecutive failures
      timeout: 60000, // Try again after 1 minute
      lastFailureTime: 0,
      successCount: 0 // For half-open state
    };
    
    // Adaptive rate limiting
    this.adaptiveThrottling = {
      currentDelay: 30,
      minDelay: 30,
      maxDelay: 1000,
      backoffMultiplier: 1.5,
      recoveryRate: 0.9,
      consecutiveErrors: 0
    };
    
    // Real-time subscription management (NEW)
    this.subscriptions = {
      globalProperties: { active: false, callback: null, lastData: null },
      blocks: { active: false, callback: null, lastData: null },
      blockHeaders: { active: false, callback: null, lastData: null },
      operations: { active: false, callback: null, lastData: null },
      witnesses: { active: false, callback: null, lastData: null }
    };
    
    // Basic connection state tracking for UI (simplified from health monitoring)
    this.connectionHealth = {
      consecutiveErrors: 0,
      lastError: null,
      totalRequests: 0,
      avgResponseTime: 0
    };
    
    // Connection state listeners for notifications
    this.connectionStateListeners = new Set();
    
    // Network status monitoring
    this.networkStatus = {
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      lastOnlineCheck: Date.now(),
      connectionQuality: 'good', // good, poor, bad
      latencyHistory: [],
      packetLoss: 0
    };
    
    // Connection resilience settings
    this.resilience = {
      maxReconnectDelay: 30000, // Max 30 seconds
      backoffMultiplier: 1.5,
      jitterRange: 0.1, // 10% jitter to prevent thundering herd
      connectionTimeout: 10000,
      pingInterval: 25000, // Ping every 25 seconds
      maxPingFailures: 3,
      adaptiveTimeouts: true
    };
    
    // Listen for online/offline events
    this.setupNetworkMonitoring();
    
    // Live data cache - populated by WebSocket broadcasts AND responses
    this.liveDataCache = {
      latestBlocks: new Map(), // blockNum -> blockData
      blockOperations: new Map(), // blockNum -> operations
      dynamicGlobalProperties: null,
      headBlockNumber: null,
      lastIrreversibleBlockNumber: null,
      lastUpdated: null
    };
    
    // Data expiry time (5 minutes for blocks, immediate for global props)
    this.BLOCK_CACHE_EXPIRE = 5 * 60 * 1000; // 5 minutes
    this.MAX_CACHED_BLOCKS = 1000; // Maximum blocks to keep in cache
    
    // Request deduplication and caching
    this.requestDeduplication = new Map(); // For deduplicating identical requests  
    this.requestCache = new Map(); // For caching recent responses
    
    // Start enhanced request processor for rate limiting
    this.startRequestProcessor();
    
    // Cleanup old cached data every 30 seconds
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredData();
    }, 30000);
  }

  // Setup network status monitoring
  setupNetworkMonitoring() {
    if (typeof window !== 'undefined') {
      // Listen for online/offline events
      window.addEventListener('online', () => {
        this.networkStatus.isOnline = true;
        this.networkStatus.lastOnlineCheck = Date.now();
        
        // Attempt to reconnect when coming back online
        if (!this.isConnected()) {
          setTimeout(() => {
            this.connect().catch(() => {});
          }, 1000);
        }
      });
      
      window.addEventListener('offline', () => {
        this.networkStatus.isOnline = false;
        this.connectionHealth.lastError = 'Internet connection lost';
        
        this.notifyConnectionStateChange({
          type: 'offline',
          reason: 'Internet connection lost',
          isConnected: false
        });
      });
    }
  }

  // Get delay multiplier based on connection quality
  getQualityDelayMultiplier() {
    switch (this.networkStatus.connectionQuality) {
      case 'poor': return 2.0;
      case 'bad': return 3.0;
      default: return 1.0;
    }
  }

  // Circuit breaker pattern implementation
  canMakeRequest() {
    const now = Date.now();
    
    switch (this.circuitBreaker.state) {
      case 'OPEN':
        if (now - this.circuitBreaker.lastFailureTime > this.circuitBreaker.timeout) {
          this.circuitBreaker.state = 'HALF_OPEN';
          this.circuitBreaker.successCount = 0;
          return true;
        }
        return false;
        
      case 'HALF_OPEN':
        return true;
        
      case 'CLOSED':
      default:
        return true;
    }
  }

  // Record request success for circuit breaker
  recordRequestSuccess() {
    this.circuitBreaker.failures = 0;
    
    if (this.circuitBreaker.state === 'HALF_OPEN') {
      this.circuitBreaker.successCount++;
      if (this.circuitBreaker.successCount >= 3) {
        this.circuitBreaker.state = 'CLOSED';
      }
    }
  }

  // Record request failure for circuit breaker
  recordRequestFailure() {
    this.circuitBreaker.failures++;
    this.circuitBreaker.lastFailureTime = Date.now();
    
    if (this.circuitBreaker.failures >= this.circuitBreaker.threshold) {
      this.circuitBreaker.state = 'OPEN';
    }
  }

  // Update connection quality based on latency and packet loss
  updateConnectionQuality(latency) {
    // Track latency history (keep last 10 measurements)
    this.networkStatus.latencyHistory.push(latency);
    if (this.networkStatus.latencyHistory.length > 10) {
      this.networkStatus.latencyHistory.shift();
    }
    
    // Calculate average latency
    const avgLatency = this.networkStatus.latencyHistory.reduce((a, b) => a + b, 0) / this.networkStatus.latencyHistory.length;
    
    // Determine connection quality
    if (avgLatency < 100) {
      this.networkStatus.connectionQuality = 'good';
    } else if (avgLatency < 500) {
      this.networkStatus.connectionQuality = 'poor';
    } else {
      this.networkStatus.connectionQuality = 'bad';
    }
  }

  // Enhanced cleanup for expired cache data
  cleanupExpiredData() {
    const now = Date.now();
    
    // Remove old request timestamps (keep only last minute)
    this.requestTimestamps = this.requestTimestamps.filter(timestamp => 
      now - timestamp < 60000
    );
    
    // Remove old blocks from cache
    for (const [blockNum, blockData] of this.liveDataCache.latestBlocks.entries()) {
      if (now - blockData.timestamp > this.BLOCK_CACHE_EXPIRE) {
        this.liveDataCache.latestBlocks.delete(blockNum);
        this.liveDataCache.blockOperations.delete(blockNum);
      }
    }
    
    // Keep only recent blocks if cache gets too large
    if (this.liveDataCache.latestBlocks.size > this.MAX_CACHED_BLOCKS) {
      const sortedBlocks = Array.from(this.liveDataCache.latestBlocks.keys()).sort((a, b) => b - a);
      const blocksToKeep = sortedBlocks.slice(0, this.MAX_CACHED_BLOCKS);
      
      for (const [blockNum] of this.liveDataCache.latestBlocks.entries()) {
        if (!blocksToKeep.includes(blockNum)) {
          this.liveDataCache.latestBlocks.delete(blockNum);
          this.liveDataCache.blockOperations.delete(blockNum);
        }
      }
    }
    
    // Remove stale broadcast listeners
    const staleTimeout = 300000; // 5 minutes
    for (const [key, listener] of this.broadcastListeners.entries()) {
      if (now - listener.lastAccessed > staleTimeout) {
        this.broadcastListeners.delete(key);
      }
    }
  }

  // Enhanced rate limiting check with adaptive throttling
  isRateLimited() {
    const now = Date.now();
    
    // Clean old timestamps
    this.requestTimestamps = this.requestTimestamps.filter(timestamp => 
      now - timestamp < 60000 // Keep only last minute
    );
    
    // Check if we're approaching the limit
    if (this.requestTimestamps.length >= this.maxRequestsPerMinute) {
      this.adaptBackoff();
      return true;
    }
    
    // Check adaptive interval between requests
    const adaptiveInterval = this.adaptiveThrottling.currentDelay;
    if (now - this.lastRequestTime < adaptiveInterval) {
      return true;
    }
    
    return false;
  }

  // Adaptive backoff mechanism
  adaptBackoff() {
    const throttling = this.adaptiveThrottling;
    throttling.consecutiveErrors++;
    throttling.currentDelay = Math.min(
      throttling.currentDelay * throttling.backoffMultiplier,
      throttling.maxDelay
    );
  }

  // Recovery mechanism for successful requests
  adaptRecovery() {
    const throttling = this.adaptiveThrottling;
    if (throttling.consecutiveErrors > 0) {
      throttling.consecutiveErrors = Math.max(0, throttling.consecutiveErrors - 1);
      throttling.currentDelay = Math.max(
        throttling.currentDelay * throttling.recoveryRate,
        throttling.minDelay
      );
    }
  }

  // Enhanced rate-limited request processor with priority
  startRequestProcessor() {
    setInterval(() => {
      if (this.requestQueue.length > 0 && !this.isRateLimited()) {
        // Sort queue by priority (high -> normal -> low)
        this.requestQueue.sort((a, b) => {
          const priorityOrder = { high: 3, normal: 2, low: 1 };
          return (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2);
        });
        
        const request = this.requestQueue.shift();
        this.processQueuedRequest(request);
      }
    }, this.MIN_REQUEST_INTERVAL);
  }

  // Process queued request with priority support
  async processQueuedRequest(request) {
    try {
      const result = await this.sendJSONRPCRequest(
        request.method, 
        request.params, 
        request.timeout, 
        request.priority || 'normal'
      );
      request.resolve(result);
    } catch (error) {
      request.reject(error);
    }
  }

  // Get singleton instance and auto-connect
  static getInstance() {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();// Auto-connect to start receiving broadcasts immediately
      WebSocketManager.instance.connect().catch(error => {});
    }
    return WebSocketManager.instance;
  }

  // Cleanup method for component unmounting
  static cleanup() {
    if (WebSocketManager.instance) {
      WebSocketManager.instance.close();
      WebSocketManager.instance = null;
    }
  }

  // Connect to WebSocket with retry logic
  async connect() {
    if (this.connection && this.connection.readyState === WebSocket.OPEN) {
      return this.connection;
    }

    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.isConnecting = true;
    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.connection = new WebSocket(this.WS_URL, [], {
          // Optimize WebSocket options
          perMessageDeflate: true, // Enable compression
          maxCompressedSize: 1024 * 1024, // 1MB max compressed message
          maxBackpressure: 64 * 1024, // 64KB backpressure buffer
          compression: 'shared', // Use shared compression context
          threshold: 1024, // Compress messages larger than 1KB
        });

        const connectTimeout = setTimeout(() => {
          if (this.connection) {
            this.connection.close();
          }
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        this.connection.onopen = () => {
          clearTimeout(connectTimeout);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          
          // Reset connection state on successful connection
          this.connectionHealth.consecutiveErrors = 0;
          this.connectionHealth.lastError = null;
          
          this.notifyConnectionStateChange({ 
            type: 'connected', 
            isConnected: true 
          });
          
          resolve(this.connection);
        };

        this.connection.onmessage = (event) => {
          try {
            const response = JSON.parse(event.data);
            
            // Update connection health metrics
            this.updateResponseMetrics();
            
            // Handle connection messages
            if (response.type === 'connection') {
              return;
            }
            
            // Handle JSON-RPC responses
            if (response.id && (response.result !== undefined || response.error)) {
              const pendingRequest = this.pendingRequests.get(response.id);
              if (pendingRequest) {
                clearTimeout(pendingRequest.timeout);
                this.pendingRequests.delete(response.id);
                
                if (response.error) {
                  this.connectionHealth.consecutiveErrors++;
                  pendingRequest.reject(new Error(response.error));
                } else {
                  this.connectionHealth.lastSuccessfulRequest = Date.now();
                  this.connectionHealth.consecutiveErrors = 0;
                  pendingRequest.resolve(response.result);
                }
              }
              return;
            }
            
            // Handle live blockchain broadcasts
            if (response.type === 'broadcast') {
              if (typeof this.handleBroadcast === 'function') {
                this.handleBroadcast(response);
              }
            }
            
            // NEW: Handle subscription data
            if (response.type === 'subscription') {
              this.handleSubscriptionData(response);
              return;
            }
            
            // Log unhandled messages but check for rate limit errors
            if (response.type === 'error' && response.error && response.error.includes('Rate limit')) {
              // Implement exponential backoff for rate limited requests
              setTimeout(() => {}, 5000);
              return;
            }
          } catch (error) {
            console.error('Error handling WebSocket message:', error);
          }
        };

        this.connection.onerror = (error) => {
          clearTimeout(connectTimeout);
          this.isConnecting = false;
          this.connectionHealth.consecutiveErrors++;
          
          // Provide meaningful error message
          const errorMessage = this.getConnectionErrorReason(error);
          
          this.connectionHealth.lastError = errorMessage;
          this.notifyConnectionStateChange({ 
            type: 'error', 
            error: errorMessage,
            isConnected: false 
          });
          reject(error);
        };

        this.connection.onclose = (event) => {
          clearTimeout(connectTimeout);
          this.isConnecting = false;
          this.connection = null;
          // Clear broadcast listeners on disconnect
          this.broadcastListeners.clear();
          
          // Provide meaningful error messages based on close code and conditions
          const errorMessage = this.getDisconnectionReason(event);
          
          this.connectionHealth.lastError = errorMessage;
          this.notifyConnectionStateChange({ 
            type: 'disconnected', 
            reason: errorMessage,
            code: event.code,
            isConnected: false 
          });
          
          // Auto-reconnect for broadcast stream with intelligent backoff
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            
            // Intelligent delay calculation with jitter and connection quality
            const baseDelay = Math.min(
              1000 * Math.pow(this.resilience.backoffMultiplier, this.reconnectAttempts), 
              this.resilience.maxReconnectDelay
            );
            
            // Add jitter to prevent thundering herd problem
            const jitter = baseDelay * this.resilience.jitterRange * (Math.random() - 0.5);
            
            // Adjust delay based on connection quality
            const qualityMultiplier = this.getQualityDelayMultiplier();
            const delay = Math.max(1000, baseDelay + jitter) * qualityMultiplier;
            
            setTimeout(() => {
              this.connect().catch(() => {});
            }, delay);
          }
        };

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  // Get meaningful disconnection reason based on close code and network conditions
  getDisconnectionReason(event) {
    const { code, reason } = event;
    
    // If we have a specific reason from the server, use it
    if (reason && reason.trim()) {
      return `Connection closed: ${reason}`;
    }
    
    // Map WebSocket close codes to user-friendly messages
    switch (code) {
      case 1000:
        return 'Connection closed normally';
      case 1001:
        return 'Server is going away or browser is navigating away';
      case 1002:
        return 'Connection closed due to protocol error';
      case 1003:
        return 'Connection closed due to unsupported data type';
      case 1005:
        return 'Connection closed - no status code provided';
      case 1006:
        // This is the most common code for network issues
        return this.getNetworkErrorReason();
      case 1007:
        return 'Connection closed due to invalid data format';
      case 1008:
        return 'Connection closed due to policy violation';
      case 1009:
        return 'Connection closed - message too large';
      case 1010:
        return 'Connection closed due to extension negotiation failure';
      case 1011:
        return 'Server encountered an unexpected condition';
      case 1012:
        return 'Server is restarting';
      case 1013:
        return 'Server is temporarily overloaded';
      case 1014:
        return 'Server acting as gateway received invalid response';
      case 1015:
        return 'Connection closed due to TLS handshake failure';
      default:
        // For unknown codes, try to determine the likely cause
        if (code >= 3000 && code <= 3999) {
          return 'Connection closed by server application';
        } else if (code >= 4000 && code <= 4999) {
          return 'Connection closed due to client application error';
        } else {
          return this.getNetworkErrorReason();
        }
    }
  }

  // Determine network-related error reason
  getNetworkErrorReason() {
    // Check if we're online using our monitoring
    if (!this.networkStatus.isOnline) {
      return 'No internet connection detected';
    }
    
    // Check browser's navigator.onLine as backup
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'No internet connection detected';
    }
    
    // Check recent connection attempts to determine if it's likely a network issue
    if (this.reconnectAttempts > 2) {
      return 'Network connection unstable or server unreachable';
    } else if (this.reconnectAttempts > 0) {
      return 'Temporary network issue - attempting to reconnect';
    }
    
    // Default network error message
    return 'Network connection lost or server unreachable';
  }

  // Get meaningful connection error reason
  getConnectionErrorReason(error) {
    // Check if we're online first using our monitoring
    if (!this.networkStatus.isOnline || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return 'No internet connection detected';
    }
    
    // If we have an error message, try to make it more user-friendly
    if (error && error.message) {
      const message = error.message.toLowerCase();
      
      if (message.includes('network') || message.includes('unreachable')) {
        return 'Server unreachable - check your internet connection';
      } else if (message.includes('timeout')) {
        return 'Connection timeout - slow internet or server issues';
      } else if (message.includes('refused') || message.includes('denied')) {
        return 'Connection refused by server';
      } else if (message.includes('dns') || message.includes('resolve')) {
        return 'Cannot resolve server address - check internet connection';
      } else {
        return `Connection error: ${error.message}`;
      }
    }
    
    // Default connection error based on reconnect attempts
    if (this.reconnectAttempts > 2) {
      return 'Repeated connection failures - check internet connection';
    } else {
      return 'Unable to connect to server - check your internet connection';
    }
  }

  // Update response metrics (simplified)
  updateResponseMetrics() {
    this.connectionHealth.totalRequests++;
    this.connectionHealth.consecutiveErrors = 0;
  }

  // NEW: Handle subscription data from server
  handleSubscriptionData(response) {
    const { subscription, data } = response;
    
    if (this.subscriptions[subscription] && this.subscriptions[subscription].active) {
      // Update cached data
      this.subscriptions[subscription].lastData = data;
      
      // Call the subscription callback
      if (this.subscriptions[subscription].callback) {
        try {
          this.subscriptions[subscription].callback(data);
        } catch (error) {
          console.error(`Error in subscription callback for ${subscription}:`, error);
        }
      }
    }
  }

  // Enhanced broadcast handler with better error handling
  handleBroadcast(broadcastData) {
    // Check if this is a broadcast message with the correct structure
    if (broadcastData.type !== 'broadcast') {
      return;
    }

    const { method, data } = broadcastData;
    
    try {
      // Cache different types of broadcast data
      switch (method) {
        case 'block_applied':
          if (data && data.block) {
            const blockData = data.block;
            const blockNum = parseInt(blockData.block_id.slice(0, 8), 16);
            this.liveDataCache.latestBlocks.set(blockNum, {
              ...blockData,
              timestamp: Date.now()
            });
          }
          break;
          
        case 'condenser_api.get_block':
          if (data) {
            // Extract block number from the block data
            let blockNum;
            if (data.block_id) {
              blockNum = parseInt(data.block_id.slice(0, 8), 16);
            } else if (data.previous) {
              blockNum = parseInt(data.previous.slice(0, 8), 16) + 1;
            }
            
            if (blockNum) {
              this.liveDataCache.latestBlocks.set(blockNum, {
                ...data,
                timestamp: Date.now()
              });}
          }
          break;
          
        case 'condenser_api.get_ops_in_block':if (data && Array.isArray(data)) {
            // Operations data is typically an array, need to determine block number
            // This might come with context or we might need to correlate with recent blocks// Try to get block number from operation context or recent global properties
            if (this.liveDataCache.headBlockNumber) {
              const blockNum = this.liveDataCache.headBlockNumber;
              this.liveDataCache.blockOperations.set(blockNum, data);}
          }
          break;
          
        case 'dynamic_global_properties_update':if (data) {
            this.liveDataCache.dynamicGlobalProperties = {
              ...data,
              timestamp: Date.now()
            };
            this.liveDataCache.headBlockNumber = data.head_block_number;
            this.liveDataCache.lastIrreversibleBlockNumber = data.last_irreversible_block_num;// Trigger broadcast listeners for immediate UI updates
            this.triggerBroadcastListeners('dynamic_global_properties_update', data);
          } else {}
          break;
          
        case 'operation_applied':
          if (data && data.block_num) {
            const blockNum = data.block_num;
            if (!this.liveDataCache.blockOperations.has(blockNum)) {
              this.liveDataCache.blockOperations.set(blockNum, []);
            }
            this.liveDataCache.blockOperations.get(blockNum).push(data);}
          break;
          
        default:}
      
      // Update last updated timestamp
      this.liveDataCache.lastUpdated = Date.now();
      
    } catch (error) {}
    
    // Notify all listeners for this broadcast type
    const listeners = this.broadcastListeners.get(method);
    if (listeners && listeners.callbacks.length > 0) {
      listeners.lastAccessed = Date.now();
      listeners.callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {}
      });
    }
    
    // Also notify global listeners
    const globalListeners = this.broadcastListeners.get('*');
    if (globalListeners && globalListeners.callbacks.length > 0) {
      globalListeners.lastAccessed = Date.now();
      globalListeners.callbacks.forEach(callback => {
        try {
          callback({ method, data });
        } catch (error) {}
      });
    }
  }

  // Subscribe to broadcast events
  subscribeToBroadcast(method, callback) {
    if (!this.broadcastListeners.has(method)) {
      this.broadcastListeners.set(method, {
        callbacks: [],
        lastAccessed: Date.now()
      });
    }
    
    const listeners = this.broadcastListeners.get(method);
    listeners.callbacks.push(callback);
    listeners.lastAccessed = Date.now();
    
    // Return unsubscribe function
    return () => {
      const listeners = this.broadcastListeners.get(method);
      if (listeners) {
        const index = listeners.callbacks.indexOf(callback);
        if (index > -1) {
          listeners.callbacks.splice(index, 1);
        }
      }
    };
  }

  // NEW: Subscribe to real-time data streams
  async subscribeToRealTimeData(type, callback) {
    if (!this.subscriptions[type]) {
      throw new Error(`Unsupported subscription type: ${type}`);
    }

    // Set up the subscription
    this.subscriptions[type].active = true;
    this.subscriptions[type].callback = callback;

    // Send subscription request to server
    try {
      await this.sendJSONRPCRequest('subscribe', [type]);
      return () => this.unsubscribeFromRealTimeData(type);
    } catch (error) {
      this.subscriptions[type].active = false;
      this.subscriptions[type].callback = null;
      throw error;
    }
  }

  // NEW: Unsubscribe from real-time data
  async unsubscribeFromRealTimeData(type) {
    if (this.subscriptions[type]) {
      this.subscriptions[type].active = false;
      this.subscriptions[type].callback = null;
      
      try {
        await this.sendJSONRPCRequest('unsubscribe', [type]);
      } catch (error) {
        console.error(`Error unsubscribing from ${type}:`, error);
      }
    }
  }

  // NEW: Get the latest cached subscription data
  getLatestSubscriptionData(type) {
    return this.subscriptions[type]?.lastData || null;
  }

  // Enhanced JSON-RPC request with improved error handling
  // Enhanced JSON-RPC request with circuit breaker and latency tracking
  async sendJSONRPCRequest(method, params = [], timeout = 15000, priority = 'normal') {
    // Check circuit breaker
    if (!this.canMakeRequest()) {
      throw new Error('Circuit breaker is open - too many recent failures');
    }

    await this.connect();
    
    if (this.connection.readyState !== WebSocket.OPEN) {
      this.recordRequestFailure();
      throw new Error('WebSocket connection not open');
    }
    
    const requestId = ++this.requestId;
    const startTime = Date.now();
    
    // Track request for rate limiting
    const now = Date.now();
    this.requestTimestamps.push(now);
    this.lastRequestTime = now;
    
    // Adaptive timeout based on connection quality
    const adaptiveTimeout = this.resilience.adaptiveTimeouts 
      ? this.getAdaptiveTimeout(timeout)
      : timeout;
    
    const request = {
      jsonrpc: '2.0',
      method: method,
      params: params,
      id: requestId
    };

    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        this.recordRequestFailure();
        reject(new Error(`WebSocket request timeout after ${adaptiveTimeout}ms for ${method}`));
      }, adaptiveTimeout);
      
      // Store the pending request with metadata
      this.pendingRequests.set(requestId, {
        resolve: (result) => {
          const latency = Date.now() - startTime;
          this.updateConnectionQuality(latency);
          this.recordRequestSuccess();
          resolve(result);
        },
        reject: (error) => {
          this.recordRequestFailure();
          reject(error);
        },
        timeout: timeoutHandle,
        method,
        params,
        priority,
        timestamp: startTime
      });
      
      // Send the request
      try {
        this.connection.send(JSON.stringify(request));
      } catch (error) {
        clearTimeout(timeoutHandle);
        this.pendingRequests.delete(requestId);
        this.recordRequestFailure();
        reject(error);
      }
    });
  }

  // Get adaptive timeout based on connection quality
  getAdaptiveTimeout(baseTimeout) {
    const qualityMultiplier = this.getQualityDelayMultiplier();
    return Math.min(baseTimeout * qualityMultiplier, 60000); // Max 60 seconds
  }

  // Get cached data with automatic retries and smart fallbacks
  async sendRequest(method, params, timeout = 15000, priority = 'normal', retries = 3) {
    let lastError;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Ensure WebSocket is connected
        await this.connect();
        
        // Check rate limiting for non-cached requests
        if (this.isRateLimited()) {
          return new Promise((resolve, reject) => {
            this.requestQueue.push({ method, params, timeout, priority, resolve, reject });
          });
        }
        
        // Create request key for deduplication
        const requestKey = `${method}:${JSON.stringify(params)}`;
        
        // Check if identical request is already pending
        if (this.requestDeduplication.has(requestKey)) {
          return this.requestDeduplication.get(requestKey);
        }
        
        let resultPromise;
        
        switch (method) {
          case 'condenser_api.get_dynamic_global_properties':
            resultPromise = this.getCachedGlobalPropertiesWithWait(timeout);
            break;
            
          case 'condenser_api.get_block':
            if (params && params[0]) {
              resultPromise = this.getCachedBlockWithWait(params[0], timeout);
            } else {
              throw new Error('Block number parameter required');
            }
            break;
            
          case 'condenser_api.get_block_header':
            if (params && params[0]) {
              resultPromise = this.getBlockHeaderViaWebSocket(params[0], timeout);
            } else {
              throw new Error('Block number parameter required');
            }
            break;
            
          case 'condenser_api.get_ops_in_block':
            if (params && params[0]) {
              resultPromise = this.getCachedBlockOperations(params[0], params[1], timeout);
            } else {
              throw new Error('Block number parameter required');
            }
            break;
            
          case 'condenser_api.get_transaction':
            if (params && params[0]) {
              resultPromise = this.getTransactionViaWebSocket(params[0], timeout);
            } else {
              throw new Error('Transaction ID parameter required');
            }
            break;
            
          default:
            throw new Error(`Method ${method} not supported by WebSocket system`);
        }
        
        // Store promise for deduplication
        this.requestDeduplication.set(requestKey, resultPromise);
        
        const result = await resultPromise;
        
        // Clean up
        this.requestDeduplication.delete(requestKey);
        
        return result;
        
      } catch (error) {
        lastError = error;
        
        // Don't retry for certain errors
        if (error.message.includes('parameter required') || 
            error.message.includes('not supported') ||
            error.message.includes('Circuit breaker')) {
          throw error;
        }
        
        // Exponential backoff for retries
        if (attempt < retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  // Get cached batch data (replacing HTTP batch API calls)
  async sendBatchRequest(requests, timeout = 15000) {
    try {
      const results = [];
      
      for (const request of requests) {
        const result = await this.sendRequest(request.method, request.params, timeout);
        results.push(result);
      }
      return results;
      
    } catch (error) {throw error;
    }
  }

  // Get cached global properties with WebSocket JSON-RPC fallback
  async getCachedGlobalPropertiesWithWait(timeout = 15000) {
    // If data is already available from broadcasts, return it immediately
    if (this.liveDataCache.dynamicGlobalProperties) {
      const { timestamp, ...data } = this.liveDataCache.dynamicGlobalProperties;return data;
    }
    try {
      // Request global properties via WebSocket JSON-RPC
      const globalProps = await this.sendJSONRPCRequest('condenser_api.get_dynamic_global_properties', [], timeout);
      
      if (globalProps) {
        // Cache the global properties
        this.liveDataCache.dynamicGlobalProperties = {
          ...globalProps,
          timestamp: Date.now()
        };
        this.liveDataCache.headBlockNumber = globalProps.head_block_number;
        this.liveDataCache.lastIrreversibleBlockNumber = globalProps.last_irreversible_block_num;
        this.liveDataCache.lastUpdated = Date.now();
        return globalProps;
      }
      
      throw new Error('No global properties data received');
      
    } catch (error) {
      // Wait for broadcast as fallback
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        
        const checkData = () => {
          if (this.liveDataCache.dynamicGlobalProperties) {
            const { timestamp, ...data } = this.liveDataCache.dynamicGlobalProperties;
            resolve(data);
            return;
          }
          
          if (Date.now() - startTime > timeout) {
            reject(new Error(`Timeout waiting for global properties (${timeout}ms)`));
            return;
          }
          
          // Check again in 100ms
          setTimeout(checkData, 100);
        };
        
        checkData();
      });
    }
  }

  // Get cached block data using WebSocket JSON-RPC requests
  async getCachedBlockWithWait(blockNumber, timeout = 10000) {
    const blockNum = parseInt(blockNumber);
    
    // If block is already in cache and has real data, return it immediately
    const existingBlock = this.liveDataCache.latestBlocks.get(blockNum);
    if (existingBlock && existingBlock.witness && existingBlock.witness !== 'unknown') {
      // Remove the cache timestamp and return the original block data
      const { cacheTimestamp, ...blockData } = existingBlock;
      return blockData;
    }
    
    // Fetch block data via WebSocket JSON-RPC
    try {
      const blockData = await this.sendJSONRPCRequest('condenser_api.get_block', [blockNum], timeout);
      
      if (blockData) {
        // Cache the block data with a separate cache timestamp
        this.liveDataCache.latestBlocks.set(blockNum, {
          ...blockData,
          cacheTimestamp: Date.now()
        });
        return blockData;
      }
      
      throw new Error('No block data received');
      
    } catch (error) {
      // No fallback - let the error propagate to show real connection issues
      throw new Error(`Failed to fetch block ${blockNum}: ${error.message}`);
    }
  }

  // Get cached global properties (original method)
  getCachedGlobalProperties() {
    if (!this.liveDataCache.dynamicGlobalProperties) {
      throw new Error('Global properties not yet received from live broadcast');
    }
    
    const { timestamp, ...data } = this.liveDataCache.dynamicGlobalProperties;
    return data;
  }

  // Get cached block data
  getCachedBlock(blockNumber) {
    const blockNum = parseInt(blockNumber);
    const blockData = this.liveDataCache.latestBlocks.get(blockNum);
    
    if (!blockData) {
      throw new Error(`Block ${blockNumber} not available in live broadcast cache`);
    }
    
    const { timestamp, ...data } = blockData;
    return data;
  }

  // Get cached block operations
  // Get cached block operations using WebSocket JSON-RPC requests
  async getCachedBlockOperations(blockNumber, onlyVirtual = true, timeout = 10000) {
    const blockNum = parseInt(blockNumber);
    const operations = this.liveDataCache.blockOperations.get(blockNum);
    
    if (operations && operations.length > 0) {
      return operations;
    }
    
    // Fetch operations via WebSocket JSON-RPC
    try {
      const opsData = await this.sendJSONRPCRequest('condenser_api.get_ops_in_block', [blockNum, onlyVirtual], timeout);
      
      const ops = opsData || [];
      
      // Cache the operations
      this.liveDataCache.blockOperations.set(blockNum, ops);
      return ops;
      
    } catch (error) {
      return []; // Return empty array on error
    }
  }

  // Get cache statistics and status
  getCacheStatus() {
    return {
      connected: this.isConnected(),
      blocksInCache: this.liveDataCache.latestBlocks.size,
      operationsInCache: this.liveDataCache.blockOperations.size,
      headBlockNumber: this.liveDataCache.headBlockNumber,
      lastIrreversibleBlockNumber: this.liveDataCache.lastIrreversibleBlockNumber,
      hasGlobalProperties: !!this.liveDataCache.dynamicGlobalProperties,
      lastUpdated: this.liveDataCache.lastUpdated,
      cacheAge: this.liveDataCache.lastUpdated ? Date.now() - this.liveDataCache.lastUpdated : null
    };
  }

  // Wait for initial data to be available
  async waitForInitialData(timeout = 30000) {
    const startTime = Date.now();
    
    return new Promise((resolve, reject) => {
      const checkData = () => {
        if (this.liveDataCache.dynamicGlobalProperties && this.liveDataCache.headBlockNumber) {resolve(this.getCacheStatus());
          return;
        }
        
        if (Date.now() - startTime > timeout) {
          reject(new Error('Timeout waiting for initial broadcast data'));
          return;
        }
        
        setTimeout(checkData, 100);
      };
      
      checkData();
    });
  }

  // Get connection status
  isConnected() {
    return this.connection && this.connection.readyState === WebSocket.OPEN;
  }

  // Add broadcast listener for real-time updates
  addBroadcastListener(eventType, callback) {
    const listenerId = `${eventType}_${Date.now()}_${Math.random()}`;
    
    this.broadcastListeners.set(listenerId, {
      eventType,
      callback,
      lastAccessed: Date.now()
    });// Return unsubscribe function
    return () => {
      this.broadcastListeners.delete(listenerId);};
  }

  // Trigger broadcast listeners when events occur
  triggerBroadcastListeners(eventType, data) {
    let triggeredCount = 0;
    
    for (const [, listener] of this.broadcastListeners.entries()) {
      if (listener.eventType === eventType) {
        try {
          listener.lastAccessed = Date.now();
          listener.callback(data);
          triggeredCount++;
        } catch (error) {}
      }
    }
    
    if (triggeredCount > 0) {}
  }

  // Get block header data via WebSocket JSON-RPC
  async getBlockHeaderViaWebSocket(blockNumber, timeout = 10000) {
    const blockNum = parseInt(blockNumber);try {
      const headerData = await this.sendJSONRPCRequest('condenser_api.get_block_header', [blockNum], timeout);
      
      if (headerData) {return headerData;
      }
      
      throw new Error('No block header data received');
      
    } catch (error) {throw error;
    }
  }

  // Get transaction data via WebSocket JSON-RPC
  async getTransactionViaWebSocket(transactionId, timeout = 10000) {
    try {
      const transactionData = await this.sendJSONRPCRequest('condenser_api.get_transaction', [transactionId], timeout);
      
      if (transactionData) {return transactionData;
      }
      
      throw new Error('No transaction data received');
      
    } catch (error) {throw error;
    }
  }

  // Enhanced cleanup with subscription management
  close() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    
    // Cleanup all active subscriptions
    for (const type in this.subscriptions) {
      if (this.subscriptions[type].active) {
        this.subscriptions[type].active = false;
        this.subscriptions[type].callback = null;
        this.subscriptions[type].lastData = null;
      }
    }
    
    // Clear pending requests
    for (const [, request] of this.pendingRequests.entries()) {
      clearTimeout(request.timeout);
      request.reject(new Error('Connection closed'));
    }
    this.pendingRequests.clear();
    this.requestDeduplication.clear();
    this.requestQueue = [];
    
    if (this.connection && this.connection.readyState !== WebSocket.CLOSED) {
      this.connection.close(1000, 'Manual close');
      this.connection = null;
    }
    
    // Clear all cached data
    this.liveDataCache.latestBlocks.clear();
    this.liveDataCache.blockOperations.clear();
    this.liveDataCache.dynamicGlobalProperties = null;
    this.broadcastListeners.clear();
    
    // Reset connection health (simplified)
    this.connectionHealth = {
      consecutiveErrors: 0,
      lastError: null,
      totalRequests: 0,
      avgResponseTime: 0
    };
  }

  // Get connection health status with enhanced metrics
  getConnectionHealth() {
    const isConnected = this.connection && this.connection.readyState === WebSocket.OPEN;
    return { 
      ...this.connectionHealth,
      isConnected,
      isHealthy: isConnected && this.connectionHealth.consecutiveErrors === 0,
      networkStatus: this.networkStatus,
      circuitBreakerState: this.circuitBreaker.state,
      connectionQuality: this.networkStatus.connectionQuality,
      averageLatency: this.networkStatus.latencyHistory.length > 0 
        ? this.networkStatus.latencyHistory.reduce((a, b) => a + b, 0) / this.networkStatus.latencyHistory.length 
        : 0,
      reconnectAttempts: this.reconnectAttempts,
      queueSize: this.requestQueue.length,
      pendingRequests: this.pendingRequests.size
    };
  }

  // NEW: Add connection state listener
  addConnectionStateListener(callback) {
    this.connectionStateListeners.add(callback);
    
    // Return cleanup function
    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  // NEW: Notify connection state change
  notifyConnectionStateChange(state) {
    this.connectionStateListeners.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('Error in connection state listener:', error);
      }
    });
  }

  // NEW: Get subscription status
  getSubscriptionStatus() {
    const status = {};
    for (const [type, sub] of Object.entries(this.subscriptions)) {
      status[type] = {
        active: sub.active,
        hasData: sub.lastData !== null,
        lastUpdate: sub.lastData ? Date.now() : null
      };
    }
    return status;
  }

  // Get detailed connection statistics for monitoring and debugging
  getConnectionStats() {
    return {
      endpoint: this.WS_URL,
      isConnected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      connectionHealth: this.getConnectionHealth(),
      rateLimit: {
        currentDelay: this.adaptiveThrottling.currentDelay,
        requestsInLastMinute: this.requestTimestamps.length,
        consecutiveErrors: this.adaptiveThrottling.consecutiveErrors,
        queueSize: this.requestQueue.length
      },
      circuitBreaker: {
        state: this.circuitBreaker.state,
        failures: this.circuitBreaker.failures,
        lastFailureTime: this.circuitBreaker.lastFailureTime,
        successCount: this.circuitBreaker.successCount
      },
      performance: {
        averageLatency: this.networkStatus.latencyHistory.length > 0 
          ? Math.round(this.networkStatus.latencyHistory.reduce((a, b) => a + b, 0) / this.networkStatus.latencyHistory.length)
          : 0,
        connectionQuality: this.networkStatus.connectionQuality,
        latencyHistory: [...this.networkStatus.latencyHistory]
      },
      cache: this.getCacheStatus(),
      pendingRequestsCount: this.pendingRequests.size,
      totalRequests: this.connectionHealth.totalRequests
    };
  }

  // Performance monitoring method
  getPerformanceMetrics() {
    return {
      averageLatency: this.networkStatus.latencyHistory.length > 0 
        ? this.networkStatus.latencyHistory.reduce((a, b) => a + b, 0) / this.networkStatus.latencyHistory.length 
        : 0,
      connectionQuality: this.networkStatus.connectionQuality,
      requestsPerMinute: this.requestTimestamps.length,
      circuitBreakerState: this.circuitBreaker.state,
      cacheHitRate: this.calculateCacheHitRate(),
      reconnectCount: this.reconnectAttempts
    };
  }

  // Calculate cache hit rate (simplified)
  calculateCacheHitRate() {
    const totalRequests = this.connectionHealth.totalRequests;
    if (totalRequests === 0) return 0;
    
    // Estimate based on cache size vs total requests
    const cacheSize = this.liveDataCache.latestBlocks.size + 
                     this.liveDataCache.blockOperations.size + 
                     (this.liveDataCache.dynamicGlobalProperties ? 1 : 0);
    
    return Math.min(cacheSize / Math.max(totalRequests, 1) * 100, 100);
  }
}

export default WebSocketManager;
